'use client';

import { useState, useEffect } from 'react';
import { Formik, Form, Field, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import {
  LuPrinter,
  LuLaptop,
  LuGraduationCap,
  LuWifi,
  LuPhone,
  LuMail,
  LuCalendar,
  LuCheck,
  LuLoader,
  LuArrowRight,
} from 'react-icons/lu';
import { Card, CardContent } from '@/components/ui/card';
import { PrintingOptionsValues, FileWithId } from '@/types/printing';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/toaster';
import React from 'react';
import dynamic from 'next/dynamic';
import { useUser } from '@clerk/nextjs';

// Dynamically import the PrintingSection component with SSR disabled
const PrintingSection = dynamic(
  () => import('@/components/printing/PrintingSection'),
  { ssr: false }
);

// Define interfaces for our types
interface ServiceFormValues {
  name: string;
  email: string;
  phone: string;
  serviceType: string;
  message: string;
  preferredDate: string;
}

interface Service {
  icon: React.ElementType;
  title: string;
  description: string;
  features: string[];
  image: string;
  overlayColor: string;
  textColor: string;
  accentColor: string;
  checkColor: string;
  checkTextColor: string;
}

interface ContactInfo {
  icon: React.ElementType;
  label: string;
  value: string;
  gradient?: string;
}

const ServiceSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
  email: Yup.string().email('Invalid email').required('Required'),
  phone: Yup.string(),
  serviceType: Yup.string().required('Required'),
  message: Yup.string(),
  preferredDate: Yup.date().required('Required'),
});

const CyberServicesPage = () => {
  const { user, isSignedIn } = useUser();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [activeService, setActiveService] = useState<number | null>(null);
  const [files, setFiles] = useState<FileWithId[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(
    null
  );
  const [printingOptions, setPrintingOptions] = useState<PrintingOptionsValues>(
    {
      paperSize: 'A4',
      paperType: 'Standard',
      colorType: 'BLACK_WHITE',
      copies: 1,
      duplexPrinting: false,
      binding: false,
      bindingType: 'Spiral',
      orientation: 'portrait',
      pageRanges: 'all',
      selectedPages: [],
    }
  );

  const services: Service[] = [
    {
      icon: LuPrinter,
      title: 'Printing & Stationery',
      description: 'High-quality printing services and office supplies',
      features: [
        'Document printing',
        'Photo printing',
        'Binding services',
        'Office supplies',
      ],
      image: '/images/pages/cyber-printing.jpg',
      overlayColor: 'from-purple-600/70 via-indigo-600/80 to-violet-900/90',
      textColor: 'text-violet-50',
      accentColor: 'from-violet-400 to-purple-600',
      checkColor: 'bg-violet-500',
      checkTextColor: 'text-white',
    },
    {
      icon: LuLaptop,
      title: 'Computer Services',
      description: 'Expert computer repair and maintenance services',
      features: [
        'Hardware repair',
        'Software installation',
        'Virus removal',
        'Data recovery',
      ],
      image: '/images/pages/cyber-computer-services.jpg',
      overlayColor: 'from-blue-600/70 via-blue-700/80 to-cyan-900/90',
      textColor: 'text-blue-50',
      accentColor: 'from-blue-400 to-cyan-600',
      checkColor: 'bg-cyan-500',
      checkTextColor: 'text-white',
    },
    {
      icon: LuGraduationCap,
      title: 'Computer Training',
      description: 'Comprehensive computer training for all skill levels',
      features: [
        'Microsoft Office',
        'Graphic design',
        'Programming basics',
        'Web development',
      ],
      image: '/images/pages/cyber-computer-training.jpg',
      overlayColor: 'from-emerald-600/70 via-teal-600/80 to-green-900/90',
      textColor: 'text-emerald-50',
      accentColor: 'from-teal-400 to-emerald-600',
      checkColor: 'bg-emerald-500',
      checkTextColor: 'text-white',
    },
    {
      icon: LuWifi,
      title: 'Internet Services',
      description: 'Fast and reliable internet connectivity solutions',
      features: [
        'High-speed browsing',
        'Video conferencing',
        'Email services',
        'File downloads',
      ],
      image: '/images/pages/cyber-internet-services.jpg',
      overlayColor: 'from-amber-500/70 via-orange-600/80 to-red-800/90',
      textColor: 'text-amber-50',
      accentColor: 'from-amber-400 to-orange-600',
      checkColor: 'bg-amber-500',
      checkTextColor: 'text-white',
    },
  ];

  const router = useRouter();

  // Ref for the services section
  const servicesRef = React.useRef<HTMLDivElement>(null);
  const formRef = React.useRef<HTMLDivElement>(null);

  // Function to scroll to services section
  const scrollToServices = () => {
    servicesRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Function to scroll to contact form
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
    // Auto-select the first service if none is selected
    if (!selectedService && services.length > 0) {
      setSelectedService(services[0].title);
    }
  };

  const handleSubmit = async (
    values: ServiceFormValues,
    { resetForm }: FormikHelpers<ServiceFormValues>
  ) => {
    setIsSubmitting(true);
    setSubmitStatus(null);
    toast.loading('Submitting your request...');

    try {
      const formData = new FormData();

      // Append form fields - safely handle null or undefined values
      Object.keys(values).forEach((key) => {
        const value = values[key as keyof ServiceFormValues];
        const stringValue =
          value !== null && value !== undefined ? String(value) : '';
        formData.append(key, stringValue);
      });

      // Append printing options if service is printing
      if (values.serviceType === 'Printing & Stationery') {
        formData.append('printingOptions', JSON.stringify(printingOptions));
        formData.append('totalPages', totalPages.toString());
        formData.append(
          'selectedPages',
          printingOptions.selectedPages.join(',')
        );
        formData.append('totalPrice', totalPrice.toString());

        // Append files
        files.forEach((file) => {
          formData.append('files', file);
        });
      }

      // Send the request to our new cyber-services API endpoint
      const response = await fetch('/api/cyber-services', {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      // Dismiss loading toast
      toast.dismiss();

      if (result.success) {
        setSubmitStatus('success');
        toast.success('Service request submitted successfully!');
        resetForm();
        setFiles([]);
        setSelectedService(null);
        setTotalPages(0);
        setTotalPrice(0);
        setPrintingOptions({
          paperSize: 'A4',
          paperType: 'Standard',
          colorType: 'BLACK_WHITE',
          copies: 1,
          duplexPrinting: false,
          binding: false,
          bindingType: 'Spiral',
          orientation: 'portrait',
          pageRanges: 'all',
          selectedPages: [],
        });
      } else {
        setSubmitStatus('error');
        toast.error(
          result.error || 'Failed to submit request. Please try again.'
        );
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.dismiss();
      toast.error('Failed to submit request. Please try again.');
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className='min-h-screen bg-linear-to-b from-[#0a1019] to-[#101828]'>
      {/* Hero Section - Enhanced Mobile Friendliness */}
      <div className='relative h-[25vh] xs:h-[30vh] sm:h-[40vh] md:h-[60vh] overflow-hidden'>
        <div className='absolute inset-0'>
          <img
            src='/images/pages/cyber-hero.jpg'
            alt='Cyber Services'
            className='h-full w-full object-cover object-center brightness-[0.8]'
          />
          <div className='absolute inset-0 bg-linear-to-b from-[#0a1019]/60 via-[#0a1019]/80 to-[#101828]'>
            {/* Add animated tech pattern overlay */}
            <div className="absolute inset-0 opacity-30 bg-[url('/images/patterns/circuit-pattern.svg')] bg-repeat animate-pulse"></div>
          </div>
        </div>
        <div className='relative h-full mx-auto max-w-7xl px-3 xs:px-4 sm:px-6'>
          <div className='flex h-full flex-col items-center justify-center text-center'>
            <h1 className='text-xl xs:text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold text-white drop-shadow-lg max-w-[90%] xs:max-w-[85%] sm:max-w-full mx-auto'>
              <span className='bg-linear-to-r from-[#00B0F0] via-[#FFA500] to-[#00B050] bg-clip-text text-transparent bg-size-[200%_auto] animate-gradient-slow'>
                Professional Cyber Services
              </span>
            </h1>
            <p className='mt-1 xs:mt-2 sm:mt-3 md:mt-6 text-xs xs:text-sm sm:text-base md:text-xl text-[#00B0F0]/80 font-medium'>
              Complete digital solutions with high-quality services
            </p>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className='relative mx-auto max-w-7xl px-3 xs:px-4 sm:px-6 py-5 xs:py-6 sm:py-8 md:py-16'>
        {/* Decorative accent line */}
        <div className='absolute top-0 left-1/3 right-1/3 xs:left-1/4 xs:right-1/4 h-1 xs:h-1.5 bg-linear-to-r from-[#00B0F0] via-[#FFA500] to-[#00B050] rounded-full shadow-md shadow-[#00B0F0]/50 animate-pulse'></div>

        {/* Contact Info Cards */}
        <div className='grid gap-2 sm:gap-3 md:gap-4 grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 mb-5 xs:mb-6 sm:mb-8 md:mb-16'>
          {[
            {
              icon: LuPhone,
              label: 'Phone',
              value: '+254 756 211 546',
              gradient: 'from-blue-400 to-violet-500',
            },
            {
              icon: LuMail,
              label: 'Email',
              value: 'cyber@qwetulands.com',
              gradient: 'from-emerald-400 to-cyan-500',
            },
            {
              icon: LuCalendar,
              label: 'Hours',
              value: 'Mon-Sat: 8am - 8pm',
              gradient: 'from-amber-400 to-orange-500',
            },
          ].map((contact: ContactInfo & { gradient: string }, index) => (
            <div
              key={index}
              className={`group relative overflow-hidden rounded-lg xs:rounded-xl bg-linear-to-br from-[#101828]/70 to-[#172033]/50 backdrop-blur-xs p-2 sm:p-3 md:p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-[#00B0F0]/15 border border-[#00B0F0]/20 hover:border-[#00B0F0]/40 ${
                index === 2
                  ? 'col-span-full xs:col-span-2 sm:col-span-1 flex justify-center'
                  : ''
              }`}
            >
              <div className='flex items-center gap-2 sm:gap-3 md:gap-4'>
                <div
                  className={`rounded-lg xs:rounded-xl bg-linear-to-br ${
                    contact.gradient
                  } p-1 xs:p-1.5 sm:p-2 md:p-3 text-white transition-transform duration-300 group-hover:scale-110 shadow-md shadow-${
                    contact.gradient.split(' ')[1]
                  }/20`}
                >
                  <contact.icon className='h-3 w-3 xs:h-3.5 xs:w-3.5 sm:h-4 sm:w-4 md:h-6 md:w-6' />
                </div>
                <div>
                  <p className='text-[10px] xs:text-xs md:text-sm text-[#00B0F0]/80 font-medium'>
                    {contact.label}
                  </p>
                  <p className='text-xs xs:text-xs sm:text-sm md:text-lg font-semibold text-white'>
                    {contact.value}
                  </p>
                </div>
              </div>
              {/* Add subtle animated accent */}
              <div className='absolute bottom-0 left-0 w-0 h-0.5 bg-linear-to-r from-transparent via-[#00B0F0] to-transparent group-hover:w-full transition-all duration-700'></div>
            </div>
          ))}
        </div>

        {/* Services Grid */}
        <div
          ref={servicesRef}
          className='grid gap-3 sm:gap-4 md:gap-6 grid-cols-1 md:grid-cols-2 mb-5 xs:mb-6 sm:mb-8 md:mb-16'
        >
          {services.map((service, idx) => (
            <div
              key={idx}
              className='group relative isolate cursor-pointer transform transition-all duration-500 hover:scale-[1.02]'
              onMouseEnter={() => setActiveService(idx)}
              onMouseLeave={() => setActiveService(null)}
              onClick={() => setSelectedService(service.title)}
            >
              <div className='relative h-[180px] xs:h-[200px] sm:h-[250px] md:h-[400px] overflow-hidden rounded-lg xs:rounded-xl sm:rounded-2xl md:rounded-3xl transition-all duration-500 shadow-xl shadow-[#00B0F0]/20 border border-transparent hover:border-indigo-500/30'>
                <img
                  src={service.image}
                  alt={service.title}
                  className='absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-in-out group-hover:scale-110 brightness-[0.85]'
                />

                {/* Gradient overlay with improved colors */}
                <div
                  className={`absolute inset-0 bg-linear-to-b ${
                    service.overlayColor
                  } opacity-70 xs:opacity-50 sm:opacity-0 transition-opacity duration-500 
                  ${
                    activeService === idx
                      ? 'opacity-95'
                      : 'group-hover:opacity-90'
                  }`}
                />

                {/* Add colorful accent line to each service card */}
                <div
                  className={`absolute top-0 left-0 right-0 h-1 xs:h-1 sm:h-2 bg-linear-to-r ${service.accentColor}`}
                ></div>

                {/* Add subtle particle effect overlay */}
                <div className="absolute inset-0 opacity-20 mix-blend-overlay bg-[url('/images/patterns/circuit-pattern.svg')] bg-repeat bg-cover"></div>

                <div className='relative h-full p-2 xs:p-3 sm:p-4 md:p-8 flex flex-col justify-end'>
                  {/* Glowing orb accent in the corner */}
                  <div className='absolute top-8 right-8 w-12 xs:w-16 sm:w-24 h-12 xs:h-16 sm:h-24 rounded-full bg-linear-to-br from-white/10 to-transparent blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-700'></div>

                  <div className='mb-1 xs:mb-2 sm:mb-3 md:mb-6'>
                    <h3
                      className='text-base xs:text-lg sm:text-xl md:text-2xl font-bold text-white mb-0.5 xs:mb-1 sm:mb-2 md:mb-4 transform transition-transform duration-500
                      group-hover:-translate-y-2'
                    >
                      <span
                        className={`bg-linear-to-r ${service.accentColor} bg-clip-text text-transparent drop-shadow-md`}
                      >
                        {service.title}
                      </span>
                    </h3>
                    <p
                      className={`transform transition-all duration-500 text-[10px] xs:text-xs sm:text-sm md:text-lg
                      group-hover:-translate-y-2 opacity-90 group-hover:opacity-100 ${service.textColor} drop-shadow-md`}
                    >
                      {service.description}
                    </p>
                  </div>

                  {/* Animated line separator */}
                  <div className='w-0 h-px xs:h-[2px] bg-linear-to-r from-white/0 via-white/70 to-white/0 group-hover:w-full transition-all duration-700 mb-1 xs:mb-2 sm:mb-3 md:mb-4'></div>

                  <ul
                    className='space-y-0.5 xs:space-y-1 sm:space-y-2 md:space-y-3 transform transition-all duration-500 
                    translate-y-8 opacity-0 group-hover:translate-y-0 group-hover:opacity-100'
                  >
                    {service.features.map((feature, i) => (
                      <li
                        key={i}
                        className='flex items-center space-x-1 sm:space-x-2 md:space-x-3 group/item'
                      >
                        <span
                          className={`shrink-0 rounded-full ${service.checkColor} p-0.5 xs:p-0.5 sm:p-1 md:p-1.5 shadow-lg shadow-${service.checkColor}/20 transition-transform duration-300 group-hover/item:scale-110`}
                        >
                          <LuCheck
                            className={`h-1.5 w-1.5 xs:h-2 xs:w-2 sm:h-3 sm:w-3 md:h-4 md:w-4 ${service.checkTextColor}`}
                          />
                        </span>
                        <span className='text-white text-[10px] xs:text-xs md:text-base font-medium drop-shadow-sm'>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Service Request Form */}
        <div ref={formRef} className='mx-auto max-w-4xl'>
          <Card className='overflow-hidden rounded-lg xs:rounded-xl sm:rounded-2xl md:rounded-3xl border-0 bg-linear-to-br from-[#0F172A]/90 to-[#1E293B]/90 backdrop-blur-xs shadow-xl border border-indigo-500/20'>
            <CardContent className='p-2.5 xs:p-3 sm:p-4 md:p-8'>
              <div className='mb-3 xs:mb-4 sm:mb-6 md:mb-8 text-center'>
                <h2 className='text-base xs:text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-white'>
                  <span className='bg-linear-to-r from-indigo-400 via-blue-300 to-purple-400 bg-clip-text text-transparent'>
                    Request Cyber Services
                  </span>
                </h2>
                <p className='mt-0.5 xs:mt-1 sm:mt-2 text-[10px] xs:text-xs sm:text-sm md:text-base text-indigo-300'>
                  Fill out the form below to request our digital services
                </p>
              </div>

              {selectedService ? (
                <Formik
                  initialValues={{
                    name: isSignedIn && user?.fullName ? user.fullName : '',
                    email:
                      isSignedIn && user?.primaryEmailAddress?.emailAddress
                        ? user.primaryEmailAddress.emailAddress
                        : '',
                    phone: '',
                    serviceType: selectedService,
                    message: '',
                    preferredDate: new Date().toISOString().split('T')[0],
                  }}
                  validationSchema={ServiceSchema}
                  onSubmit={handleSubmit}
                  enableReinitialize
                >
                  {({ errors, touched, isValid, setFieldValue, values }) => (
                    <Form className='space-y-2.5 xs:space-y-3 sm:space-y-4 md:space-y-6'>
                      <div className='grid gap-2.5 xs:gap-3 sm:gap-4 md:gap-6 grid-cols-1 xs:grid-cols-1 sm:grid-cols-2'>
                        <div>
                          <label
                            htmlFor='name'
                            className='mb-0.5 xs:mb-1 md:mb-2 block text-[10px] xs:text-xs md:text-sm font-medium text-indigo-300'
                          >
                            Full Name
                          </label>
                          <Field
                            name='name'
                            className='w-full rounded-md xs:rounded-lg border border-indigo-500/30 bg-[#101828]/50 p-2 xs:p-2.5 md:p-3 text-[11px] xs:text-xs sm:text-sm md:text-base text-white placeholder-indigo-400/50 focus:border-indigo-500 focus:ring-3 focus:ring-indigo-500/20 transition-all duration-300 hover:border-indigo-500/50'
                            placeholder='John Doe'
                            readOnly={isSignedIn}
                          />
                          {errors.name && touched.name && (
                            <div className='mt-0.5 xs:mt-1 text-[10px] xs:text-xs md:text-sm text-red-400'>
                              {errors.name}
                            </div>
                          )}
                        </div>

                        <div>
                          <label
                            htmlFor='email'
                            className='mb-0.5 xs:mb-1 md:mb-2 block text-[10px] xs:text-xs md:text-sm font-medium text-indigo-300'
                          >
                            Email Address
                          </label>
                          <Field
                            name='email'
                            type='email'
                            className='w-full rounded-md xs:rounded-lg border border-indigo-500/30 bg-[#101828]/50 p-2 xs:p-2.5 md:p-3 text-[11px] xs:text-xs sm:text-sm md:text-base text-white placeholder-indigo-400/50 focus:border-indigo-500 focus:ring-3 focus:ring-indigo-500/20 transition-all duration-300 hover:border-indigo-500/50'
                            placeholder='john@example.com'
                            readOnly={isSignedIn}
                          />
                          {errors.email && touched.email && (
                            <div className='mt-0.5 xs:mt-1 text-[10px] xs:text-xs md:text-sm text-red-400'>
                              {errors.email}
                            </div>
                          )}
                        </div>

                        <div>
                          <label
                            htmlFor='phone'
                            className='mb-0.5 xs:mb-1 md:mb-2 block text-[10px] xs:text-xs md:text-sm font-medium text-indigo-300'
                          >
                            Phone Number
                          </label>
                          <Field
                            name='phone'
                            className='w-full rounded-md xs:rounded-lg border border-indigo-500/30 bg-[#101828]/50 p-2 xs:p-2.5 md:p-3 text-[11px] xs:text-xs sm:text-sm md:text-base text-white placeholder-indigo-400/50 focus:border-indigo-500 focus:ring-3 focus:ring-indigo-500/20 transition-all duration-300 hover:border-indigo-500/50'
                            placeholder='+254 7XX XXX XXX'
                          />
                          {errors.phone && touched.phone && (
                            <div className='mt-0.5 xs:mt-1 text-[10px] xs:text-xs md:text-sm text-red-400'>
                              {errors.phone}
                            </div>
                          )}
                        </div>

                        <div>
                          <label
                            htmlFor='serviceType'
                            className='mb-0.5 xs:mb-1 md:mb-2 block text-[10px] xs:text-xs md:text-sm font-medium text-indigo-300'
                          >
                            Service Type
                          </label>
                          <Field
                            as='select'
                            name='serviceType'
                            className='w-full rounded-md xs:rounded-lg border border-indigo-500/30 bg-[#101828]/50 p-2 xs:p-2.5 md:p-3 text-[11px] xs:text-xs sm:text-sm md:text-base text-white focus:border-indigo-500 focus:ring-3 focus:ring-indigo-500/20 transition-all duration-300 hover:border-indigo-500/50'
                          >
                            {services.map((service) => (
                              <option
                                key={service.title}
                                value={service.title}
                                className='bg-[#101828] text-[11px] xs:text-xs sm:text-sm md:text-base'
                              >
                                {service.title}
                              </option>
                            ))}
                          </Field>
                        </div>

                        <div>
                          <label
                            htmlFor='preferredDate'
                            className='mb-0.5 xs:mb-1 md:mb-2 block text-[10px] xs:text-xs md:text-sm font-medium text-indigo-300'
                          >
                            Preferred Date & Time
                          </label>
                          <Field
                            name='preferredDate'
                            type='datetime-local'
                            className='w-full rounded-md xs:rounded-lg border border-indigo-500/30 bg-[#101828]/50 p-2 xs:p-2.5 md:p-3 text-[11px] xs:text-xs sm:text-sm md:text-base text-white focus:border-indigo-500 focus:ring-3 focus:ring-indigo-500/20 transition-all duration-300 hover:border-indigo-500/50'
                          />
                          {errors.preferredDate && touched.preferredDate && (
                            <div className='mt-0.5 xs:mt-1 text-[10px] xs:text-xs md:text-sm text-red-400'>
                              {errors.preferredDate}
                            </div>
                          )}
                        </div>

                        {/* Printing Options Section */}
                        {values.serviceType === 'Printing & Stationery' && (
                          <div className='col-span-1 xs:col-span-1 sm:col-span-2 mt-1 xs:mt-2 sm:mt-3'>
                            <div className='border border-indigo-500/20 rounded-md xs:rounded-lg p-2 xs:p-2.5 sm:p-3 md:p-4 bg-[#0F172A]/30'>
                              <h3 className='text-[11px] xs:text-xs sm:text-sm md:text-base font-medium text-indigo-600 mb-1.5 xs:mb-2 sm:mb-3'>
                                Printing Options
                              </h3>
                              <PrintingSection
                                onPrintingOptionsChange={setPrintingOptions}
                                onFilesChange={setFiles}
                                onTotalPagesChange={setTotalPages}
                                onTotalPriceChange={setTotalPrice}
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      <div>
                        <label
                          htmlFor='message'
                          className='mb-0.5 xs:mb-1 md:mb-2 block text-[10px] xs:text-xs md:text-sm font-medium text-indigo-300'
                        >
                          Additional Requirements
                        </label>
                        <Field
                          as='textarea'
                          name='message'
                          rows={3}
                          className='w-full rounded-md xs:rounded-lg border border-indigo-500/30 bg-[#101828]/50 p-2 xs:p-2.5 md:p-3 text-[11px] xs:text-xs sm:text-sm md:text-base text-white placeholder-indigo-400/50 focus:border-indigo-500 focus:ring-3 focus:ring-indigo-500/20 transition-all duration-300 hover:border-indigo-500/50'
                          placeholder='Any specific requirements or questions about our services...'
                        />
                      </div>

                      <div className='flex justify-end pt-1 xs:pt-1.5 sm:pt-2'>
                        <button
                          type='button'
                          onClick={() => setSelectedService(null)}
                          className='mr-2 xs:mr-2.5 sm:mr-3 md:mr-4 rounded-md xs:rounded-lg border border-indigo-400 px-3 xs:px-3.5 sm:px-4 md:px-6 py-1.5 xs:py-2 sm:py-2 md:py-3 text-[11px] xs:text-xs md:text-sm text-indigo-300 transition-colors hover:bg-indigo-900/40 min-w-[60px] xs:min-w-[70px] sm:min-w-[80px]'
                        >
                          Cancel
                        </button>
                        <button
                          type='submit'
                          disabled={isSubmitting || !isValid}
                          className='flex items-center justify-center space-x-1 md:space-x-2 rounded-md xs:rounded-lg bg-linear-to-r from-indigo-600 to-blue-500 px-3 xs:px-3.5 sm:px-4 md:px-6 py-1.5 xs:py-2 sm:py-2 md:py-3 text-[11px] xs:text-xs md:text-sm text-white transition-all hover:from-indigo-500 hover:to-purple-500 disabled:opacity-70 hover:shadow-lg hover:shadow-indigo-300/30 hover:scale-105 min-w-[100px] xs:min-w-[120px] sm:min-w-[140px]'
                        >
                          {isSubmitting ? (
                            <>
                              <LuLoader className='h-3 w-3 xs:h-3.5 xs:w-3.5 sm:h-4 sm:w-4 md:h-5 md:w-5 animate-spin' />
                              <span>Submitting...</span>
                            </>
                          ) : (
                            <>
                              <span>Submit Request</span>
                              <LuArrowRight className='h-3 w-3 xs:h-3.5 xs:w-3.5 sm:h-4 sm:w-4 md:h-5 md:w-5' />
                            </>
                          )}
                        </button>
                      </div>
                    </Form>
                  )}
                </Formik>
              ) : (
                <div className='text-center'>
                  <p className='mb-2.5 xs:mb-3 sm:mb-4 md:mb-6 text-[11px] xs:text-xs sm:text-sm md:text-base text-indigo-300'>
                    Please select a service from the options above to request a
                    quote or consultation
                  </p>
                  <div className='grid gap-2 xs:gap-2.5 md:gap-4 grid-cols-2 xs:grid-cols-2 sm:grid-cols-4'>
                    {services.map((service) => (
                      <button
                        key={service.title}
                        onClick={() => setSelectedService(service.title)}
                        className='group flex flex-col items-center rounded-md xs:rounded-xl bg-[#0F172A]/50 p-2 xs:p-2.5 sm:p-3 md:p-4 transition-all duration-300 hover:bg-indigo-500/10 border border-indigo-500/20 hover:border-indigo-500/40 hover:shadow-lg hover:shadow-indigo-500/10 hover:scale-105'
                      >
                        <div className='mb-1.5 xs:mb-2 sm:mb-2.5 md:mb-3 rounded-full bg-linear-to-br from-indigo-400 to-blue-500 p-1.5 xs:p-2 sm:p-2.5 md:p-3 text-white transition-transform duration-300 group-hover:scale-110 group-hover:shadow-md group-hover:from-indigo-300 group-hover:to-blue-400'>
                          <service.icon className='h-3 w-3 xs:h-3.5 xs:w-3.5 sm:h-4 sm:w-4 md:h-6 md:w-6' />
                        </div>
                        <span className='text-white text-[11px] xs:text-xs md:text-sm font-medium'>
                          {service.title}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      <Toaster />
    </div>
  );
};

export default CyberServicesPage;
